import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import format from 'utils/dateFns/format'
import { parse } from 'date-fns'
import { StatusBadge } from 'modules/Tickets/List/subcomponents/StatusBadge'
import styles from 'modules/Tickets/Ticket/ticket.module.scss'
import Message from './subcomponents/Message'
import NewTicketMessage, { validationSchema } from 'modules/Forms/Tickets/NewTicketMessage'
import { useTranslation } from 'react-i18next'
import validator from 'utils/asyncValidator'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import {
  CLEAR,
  CLOSE,
  FETCH_TICKET,
  PANEL_NAVIGATE,
  REOPEN,
  SET_DATA,
  SET_LOADING,
} from 'modules/Tickets/types'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import clsx from 'clsx'
import { initialize } from 'redux-form'
import GridSpinner from 'components/UI/GridSpinner'

const Ticket = ({ params = {} }) => {
  const { id = null } = params

  const dispatch = useDispatch()

  const { t } = useTranslation()
  const isNew = useMemo(() => !id, [id])
  const breakpoint = useBreakpoint()
  const messageContainerRef = useRef(null)
  const [smallHeader, setSmallHeader] = useState(false)

  let lastScrollTop = 0
  let lock = false

  const handleScroll = () => {
    global.requestAnimationFrame(() => {
      if (
        !messageContainerRef.current ||
        lock === true ||
        messageContainerRef.current.clientHeight > messageContainerRef.current.scrollHeight + 150
      ) {
        return
      }

      lock = true
      setSmallHeader(lastScrollTop > messageContainerRef.current.scrollTop)
      lastScrollTop = messageContainerRef.current.scrollTop

      setTimeout(() => {
        lock = false
      }, 1000)
    })
  }

  useLayoutEffect(() => {
    messageContainerRef.current &&
      messageContainerRef.current.addEventListener('scroll', debounce(handleScroll, 250))

    return () => {
      messageContainerRef.current &&
        messageContainerRef.current.removeEventListener('scroll', debounce(handleScroll, 250))
    }
  }, [messageContainerRef.current])

  const {
    title,
    status,
    created_at,
    closed_at,
    updated_at,
    messages = [],
    loading,
  } = useSelector(({ ticket }) => ticket)

  useEffect(() => {
    if (id) {
      dispatch({ type: FETCH_TICKET, payload: { id } })
    } else {
      dispatch({
        type: SET_LOADING,
        payload: { loading: false },
      })
    }

    return () => {
      dispatch({ type: CLEAR })
    }
  }, [id])

  //reverse for correct rendering as chat
  const messagesList = useMemo(() => messages.reverse(), [messages])

  useEffect(() => {
    dispatch(
      initialize(
        'NewTicketMessageForm',
        isNew
          ? {
              title: params.title ?? '',
              text: params.text ?? '',
              attachments: [],
            }
          : {
              text: '',
              attachments: [],
            },
        false,
      ),
    )
  }, [isNew, params])

  return (
    <>
      <Grid
        direction={'column'}
        className={styles['ticket-container']}
      >
        <Grid
          gap={16}
          className={clsx(styles['header-container'], {
            [styles['header-container_small']]: smallHeader,
          })}
        >
          <Button
            variant={'link'}
            color={'Label-Fifth'}
            onClick={() => dispatch({ type: PANEL_NAVIGATE, payload: { route: 'list' } })}
            className={styles['header__back-button']}
            size={'s'}
            startIcon={<Icon name={'arrow-left'} />}
          >
            {t('common::go_back')}
          </Button>
          <Grid
            variant={breakpoint.sm ? 'Header-40' : 'Header-60'}
            component={Typography}
            className={clsx(styles['header'], {
              [styles['header_small']]: smallHeader,
            })}
          >
            {isNew ? t('tickets::new_ticket') : title}
          </Grid>

          {!loading && !isNew && (
            <Grid
              className={'margin-bottom-24'}
              justifyContent={'stretch'}
              alignItems={'center'}
              wrap={breakpoint.sm ? 'wrap' : 'nowrap'}
              gap={16}
            >
              <Grid
                wrap={breakpoint.sm}
                alignItems={'center'}
                gap={16}
              >
                <StatusBadge
                  size={'xs'}
                  variant={'Body-10'}
                  status={status}
                  className={styles['header__status-badge']}
                />
                <Typography
                  component={'div'}
                  variant={'Body-10'}
                  noMargin
                  color={'Label-Fifth'}
                  className={styles['header__ticket-id']}
                >
                  #{id}
                </Typography>

                {!smallHeader && (
                  <Typography
                    component={'div'}
                    variant={'Body-10'}
                    noMargin
                    color={'Label-Secondary'}
                  >
                    {t('common::updated_at')}:{' '}
                    {created_at &&
                      format(
                        parse(updated_at, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date()),
                        'HH:mm, d.MM.yyyy',
                      )}
                  </Typography>
                )}
              </Grid>

              {!isNew && status !== 'closed' && (
                <Button
                  fullWidth={breakpoint.sm}
                  size={'s'}
                  variant={'secondary'}
                  grey
                  noWrap
                  onClick={() =>
                    dispatch({
                      type: CLOSE,
                      payload: { id },
                    })
                  }
                >
                  {t('tickets::close_ticket')}
                </Button>
              )}

              {status === 'closed' && (
                <Button
                  fullWidth={breakpoint.sm}
                  size={'s'}
                  variant={'secondary'}
                  grey
                  noWrap
                  onClick={() =>
                    dispatch({
                      type: REOPEN,
                      payload: { id },
                    })
                  }
                >
                  {t('tickets::reopen_ticket')}
                </Button>
              )}
            </Grid>
          )}
        </Grid>

        {loading && <GridSpinner />}
        <div
          className={clsx(styles['messages-container'], {
            [styles['messages-container_not-stretched']]: status === 'closed',
          })}
          ref={messageContainerRef}
        >
          {status === 'closed' && closed_at && (
            <Grid justifyContent={'center'}>
              <Typography
                color={'Label-Fifth'}
                variant={'Body-15'}
                className={styles['closed-at-notification']}
              >
                {t('tickets::ticket_closed_at', {
                  date: format(
                    parse(closed_at, "yyyy-MM-dd'T'HH:mm:ssXXX", new Date()),
                    'HH:mm, d.MM.yyyy',
                  ),
                })}
              </Typography>
            </Grid>
          )}
          {messages.length > 0 &&
            messagesList.map((message, i) => (
              <Message
                key={'message-' + i}
                {...message}
              />
            ))}
        </div>

        {status !== 'closed' && (
          <Grid className={clsx(styles['new-message-container'])}>
            <NewTicketMessage
              newTicket={isNew}
              id={id}
              asyncValidate={validator(validationSchema(isNew))}
              onSuccess={(ticketState) =>
                isNew
                  ? dispatch({
                      type: PANEL_NAVIGATE,
                      payload: {
                        route: 'ticket',
                        params: { id: ticketState.id },
                      },
                    })
                  : dispatch({
                      type: SET_DATA,
                      payload: ticketState,
                    })
              }
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default Ticket

Ticket.propTypes = {
  params: PropTypes.object,
}
